import { inject, Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { catchError, tap } from 'rxjs';

import { CheckUserExistsResult } from '../models';
import { MemberService } from '../services/data-services';
import {
  CheckUserExistsByEmail,
  CheckUserExistsByEmailFail,
  CheckUserExistsByEmailSuccess,
  ResetUserExistenceCheck,
} from './actions';

export interface MemberStateModel {
  userExistenceStatus: CheckUserExistsResult;
  isCheckingUserExistence: boolean;
  userExistenceCheckError?: string;
  userExistenceCheckCompleted: boolean;
}

const defaultState: MemberStateModel = {
  userExistenceStatus: {
    email: '',
    isUserExist: false,
  },
  isCheckingUserExistence: false,
  userExistenceCheckCompleted: false,
};

@State<MemberStateModel>({
  name: 'member',
  defaults: defaultState,
})
@Injectable()
export class MemberState {
  private readonly memberService = inject(MemberService);

  @Action(CheckUserExistsByEmail)
  checkUserExistsByEmail(
    ctx: StateContext<MemberStateModel>,
    { email, selectedCompanyId }: CheckUserExistsByEmail,
  ) {
    ctx.patchState({
      isCheckingUserExistence: true,
      userExistenceCheckCompleted: false,
    });

    return this.memberService
      .checkUserExistsByEmail(email, selectedCompanyId)
      .pipe(
        tap((data) => {
          ctx.dispatch(new CheckUserExistsByEmailSuccess(data.data));
        }),
        catchError((err) => ctx.dispatch(new CheckUserExistsByEmailFail(err))),
      );
  }

  @Action(CheckUserExistsByEmailSuccess)
  checkUserExistsByEmailSuccess(
    ctx: StateContext<MemberStateModel>,
    { isUserExists }: CheckUserExistsByEmailSuccess,
  ) {
    ctx.patchState({
      userExistenceStatus: isUserExists,
      isCheckingUserExistence: false,
      userExistenceCheckError: '',
      userExistenceCheckCompleted: true,
    });
  }

  @Action(CheckUserExistsByEmailFail)
  checkUserExistsByEmailFail(
    ctx: StateContext<MemberStateModel>,
    { error }: CheckUserExistsByEmailFail,
  ) {
    ctx.patchState({
      userExistenceCheckError: error,
      isCheckingUserExistence: false,
      userExistenceCheckCompleted: true,
    });
  }

  @Action(ResetUserExistenceCheck)
  resetUserExistenceCheck(ctx: StateContext<MemberStateModel>) {
    ctx.patchState({
      userExistenceStatus: {
        email: '',
        isUserExist: false,
      },
      isCheckingUserExistence: false,
      userExistenceCheckCompleted: false,
      userExistenceCheckError: undefined,
    });
  }
}
